import React, { useState } from 'react';
import FooterDrawer from '@/components/ui/atoms/FooterDrawer';
import { IconCheck } from '@tabler/icons-react';
import { dialCodeOptions } from '@/utils/countryCodes';
import styles from './MDialCode.module.css';

// Types
export interface MDialCodeProps {
  open: boolean;
  onClose: () => void;
  onDone: (code: string) => void;
  initialValue?: string;
  title?: string;
}

// Utility to get emoji flag from country code
function getFlagEmoji(countryCode: string) {
  const codeToCountry: Record<string, string> = {
    '+91': 'IN', '+1': 'US', '+44': 'GB', '+61': 'AU', '+81': 'JP', 
    '+971': 'AE', '+65': 'SG', '+49': 'DE', '+33': 'FR', '+86': 'CN',
  };
  
  const cc = codeToCountry[countryCode] || '';
  if (cc.length === 2) {
    // Convert country code to regional indicator symbols
    return String.fromCodePoint(...[...cc.toUpperCase()].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));
  }
  return '🏳️';
}

const getDialCodeOptions = () => dialCodeOptions.map(opt => {
  // Parse: label: '+91 (India)' or '+91 India'
  let code = opt.value;
  let country = opt.label;
  if (country.includes('(') && country.includes(')')) {
    country = country.split('(')[1].split(')')[0];
  } else if (country.includes(' ')) {
    country = country.split(' ').slice(1).join(' ');
  }
  return {
    flag: getFlagEmoji(code),
    code,
    country,
    fullLabel: opt.label,
  };
});

export const MDialCode: React.FC<MDialCodeProps> = ({
  open,
  onClose,
  onDone,
  initialValue = '+91',
  title = 'Select Country Code',
}) => {
  const [selectedCode, setSelectedCode] = useState(initialValue);
  const options = getDialCodeOptions();

  const handleCodeSelect = (code: string) => {
    setSelectedCode(code);
    onDone(code); // Call onDone immediately when selection is made
  };

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title={title}
      showClose={true}
      onDone={undefined}
      height="500px"
    >
      <div className={styles.dialCodeWrap}>
        {options.map((option) => (
          <div
            key={option.code}
            className={`${styles.dialCodeOption} ${
              selectedCode === option.code ? styles.selected : ''
            }`}
            onClick={() => handleCodeSelect(option.code)}
            role="button"
            tabIndex={0}
            aria-selected={selectedCode === option.code}
          >
            <div className={styles.dialCodeContent}>
              <span className={styles.flag}>{option.flag}</span>
              <span className={styles.code}>{option.code}</span>
              <span className={styles.country}>{option.country}</span>
            </div>
            {selectedCode === option.code && (
              <IconCheck size={24} className={styles.checkmark} />
            )}
          </div>
        ))}
      </div>
    </FooterDrawer>
  );
};

export default MDialCode;
